import { FiltersWidget as FiltersWidgetComponent } from '@repo/go-widgets/filters';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import i18n from 'src/app/i18n';
import { useService } from 'src/packages/di';

import { WidgetHeader } from '../widget-header/WidgetHeader';
import { PinWidgetButton } from '../workbench/pin-widget-button/PinWidgetButton';
import type { TWidgetRendererOptions } from '../workspace/services/WidgetRendererService';

import type { FiltersWidgetEntity } from './FiltersWidget.entity';
import styles from './FiltersWidget.module.scss';
import { FiltersWidgetStore } from './FiltersWidget.store';

type WrapperProps = {
  entity: FiltersWidgetEntity;
  options: TWidgetRendererOptions;
};

type Props = {
  store: FiltersWidgetStore;
  entity: FiltersWidgetEntity;
  options: TWidgetRendererOptions;
};

export const FiltersWidgetWrapper = observer(function FiltersWidgetWrapper({ entity, options }: WrapperProps) {
  const [store, setStore] = useState<FiltersWidgetStore | null>(null);
  const storeService = useService('widgetStoreService');

  useEffect(() => {
    if (store) return;

    const existing = storeService.getStore<FiltersWidgetEntity, FiltersWidgetStore>(entity);

    if (existing) {
      setStore(existing);
    } else {
      const newStore = new FiltersWidgetStore(entity, options.groupSelectStore);
      storeService.setStore<FiltersWidgetEntity, FiltersWidgetStore>(entity, newStore);
      setStore(newStore);
    }
  }, [setStore, storeService, entity, options.groupSelectStore, store]);

  if (!store) return null;

  return <FiltersWidgetView store={store} entity={entity} options={options} />;
});

export const FiltersWidgetView = observer(function FiltersWidgetView({ entity, options, store }: Props) {
  const language = useService('language');

  const handlePinWidget = () => options.widgetsDisplayingPriorityManager.togglePinWidget(entity);

  useEffect(() => store.effect(), [store, store.effect]);

  return (
    <div className={styles.filtersWidgetWrapper}>
      <WidgetHeader
        entity={entity}
        onWidgetChangeGroup={store.setGroupForFilters}
        groupSelectStore={options.groupSelectStore}
        widgetName={entity.title}
        onWidgetDelete={() => {
          options.onWidgetDelete(entity);
        }}
        renderAdditionalButtons={() => (
          <>{entity.isControlWidget && <PinWidgetButton isPinned={entity.isPinned} onClick={handlePinWidget} />}</>
        )}
      />

      <div className={styles.wrapper}>
        <div className={styles.filtersRoot}>
          <FiltersWidgetComponent
            filtersService={store.filtersService}
            language={language.language}
            parentI18n={i18n}
            initialState={entity.stateParams}
            onStateChange={store.setStateParams}
          />
        </div>
      </div>
    </div>
  );
});
